import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import type { BudgetSheetState } from '../../../../../../../services/projectAutofillDraftService.ts'
import type {
  CommittedDraftSnapshot,
  DraftValidationShape,
  ProjectAutofillPayload,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  clampIsoDateToMax,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotSourcesHaveData,
  documentationSlotsHavePages,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizeDocumentationSlotSources,
  normalizePageList,
  serializeRubrosSnapshot
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { toHtmlDateInputValue } from '../../../../../../../utils/toHtmlDateInputValue.ts'

type SaveState = 'idle' | 'ok' | 'error'

export type UseProjectAutofillPayloadHydrationArgs = {
  payload: ProjectAutofillPayload
  committedRef: MutableRefObject<CommittedDraftSnapshot | null>
  setName: Dispatch<SetStateAction<string>>
  setDescription: Dispatch<SetStateAction<string>>
  setZone: Dispatch<SetStateAction<string>>
  setStartDate: Dispatch<SetStateAction<string>>
  setEndDate: Dispatch<SetStateAction<string>>
  setRubros: Dispatch<SetStateAction<RubroRow[]>>
  setFuzzyConfirmByRubro: Dispatch<SetStateAction<Record<string, boolean>>>
  setPatchedDraftSync: Dispatch<
    SetStateAction<{
      validation?: DraftValidationShape
      collectedData?: ProjectAutofillPayload['collectedData']
    } | null>
  >
  setExpandedRubros: Dispatch<SetStateAction<Record<string, boolean>>>
  setSaveState: Dispatch<SetStateAction<SaveState>>
}

export function useProjectAutofillPayloadHydration(args: UseProjectAutofillPayloadHydrationArgs) {
  const {
    payload,
    committedRef,
    setName,
    setDescription,
    setZone,
    setStartDate,
    setEndDate,
    setRubros,
    setFuzzyConfirmByRubro,
    setPatchedDraftSync,
    setExpandedRubros,
    setSaveState
  } = args

  useEffect(() => {
    const nextName = payload.draft?.project?.name || ''
    const nextDescription = payload.draft?.project?.description || ''
    const nextZone = payload.draft?.project?.zone || ''
    const nextStart = toHtmlDateInputValue(payload.draft?.project?.startDate ?? '')
    const nextEnd = toHtmlDateInputValue(payload.draft?.project?.endDate ?? '')
    setName(nextName)
    setDescription(nextDescription)
    setZone(nextZone)
    setStartDate(nextStart)
    setEndDate(nextEnd)
    const docPreview = payload.draft?.metadata?.documentationPreview
    const nextRubros = (payload.draft?.subprojects || []).map((sp, idx) => {
      const inherited = sp.datesInheritedFromProject === true
      let s =
        toHtmlDateInputValue(sp.startDate || (inherited ? nextStart : '') || '') || ''
      let e = toHtmlDateInputValue(sp.endDate || (inherited ? nextEnd : '') || '') || ''
      if (nextEnd) {
        if (s) s = clampIsoDateToMax(s, nextEnd)
        if (e) e = clampIsoDateToMax(e, nextEnd)
      }
      if (s && e && e < s) e = s
      const tid = sp.tempId || `rubro-${idx}`
      const spRec = sp as Record<string, unknown>
      const slotsFromSp = normalizeDocumentationSlots(sp.documentationSlots)
      let documentationSlots = slotsFromSp
      let sourcePagesNorm = normalizePageList(sp.sourcePages)
      let sourceFileId = spRec.sourceFileId != null ? String(spRec.sourceFileId).trim() : ''
      let documentationSlotSources = normalizeDocumentationSlotSources(spRec.documentationSlotSources)
      const assignmentForRubro = docPreview?.assignments?.find(
        (x) => String(x?.tempId ?? '') === String(tid)
      )
      if (assignmentForRubro && !documentationSlotsHavePages(slotsFromSp)) {
        documentationSlots = normalizeDocumentationSlots(assignmentForRubro.documentationSlots)
        if (!sourcePagesNorm?.length) {
          sourcePagesNorm = normalizePageList(assignmentForRubro.sourcePages)
        }
      }
      if (!sourceFileId && assignmentForRubro?.sourceFileId) {
        sourceFileId = String(assignmentForRubro.sourceFileId).trim()
      }
      if (!documentationSlotSourcesHaveData(documentationSlotSources) && assignmentForRubro) {
        documentationSlotSources = normalizeDocumentationSlotSources(
          assignmentForRubro.documentationSlotSources
        )
      }
      const sourceFileIdOut = sourceFileId || undefined
      const documentationSlotSourcesOut = documentationSlotSourcesHaveData(documentationSlotSources)
        ? documentationSlotSources
        : undefined
      const matchedSkillNameFromBack =
        String(sp.matchedSkillName || '').trim() ||
        String(assignmentForRubro?.matchedSkillName || '').trim() ||
        undefined
      return {
        tempId: tid,
        detectedLabel: sp.detectedLabel || '',
        selectedSkillId: sp.selectedSkillId,
        description: sp.description || '',
        status: (sp.status as 'OPEN' | 'CLOSE') || 'OPEN',
        skillMatchKind: sp.skillMatchKind ?? assignmentForRubro?.skillMatchKind,
        matchedSkillName: matchedSkillNameFromBack,
        matchScore: sp.matchScore,
        name: sp.name,
        link: sp.link,
        startDate: s,
        endDate: e,
        datesInheritedFromProject: sp.datesInheritedFromProject,
        documentationSlots,
        sourcePages: sourcePagesNorm,
        ...(sourceFileIdOut ? { sourceFileId: sourceFileIdOut } : {}),
        ...(documentationSlotSourcesOut ? { documentationSlotSources: documentationSlotSourcesOut } : {}),
        ...(() => {
          const dfIds = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
          const hasDf = DOCUMENTATION_SLOT_KEYS.some((k) => !!dfIds[k])
          return hasDf ? { documentationFileIds: dfIds } : {}
        })(),
        ...(sp.budgetSheetState != null &&
        typeof sp.budgetSheetState === 'object' &&
        !Array.isArray(sp.budgetSheetState)
          ? { budgetSheetState: sp.budgetSheetState as BudgetSheetState }
          : {})
      }
    })
    setRubros(nextRubros)
    setFuzzyConfirmByRubro({})
    setPatchedDraftSync(null)
    committedRef.current = {
      name: nextName,
      description: nextDescription,
      zone: nextZone,
      startDate: nextStart,
      endDate: nextEnd,
      rubrosKey: serializeRubrosSnapshot(nextRubros),
      rubrosCount: nextRubros.length
    }
    setSaveState('idle')
    setExpandedRubros((prev) => {
      const next: Record<string, boolean> = {}
      nextRubros.forEach((r) => {
        if (prev[r.tempId]) next[r.tempId] = true
      })
      return next
    })
  }, [
    payload.draft?.project?.name,
    payload.draft?.project?.description,
    payload.draft?.project?.zone,
    payload.draft?.project?.startDate,
    payload.draft?.project?.endDate,
    payload.draft?.subprojects,
    payload.draft?.metadata?.documentationPreview,
    setName,
    setDescription,
    setZone,
    setStartDate,
    setEndDate,
    setRubros,
    setFuzzyConfirmByRubro,
    setPatchedDraftSync,
    committedRef,
    setSaveState,
    setExpandedRubros
  ])
}
